import { createOption, normalizeText } from "../../utils/CustomSearch";
import { Option, getErrors } from "../../utils/Form";
import { MAX_TEXT_LENGTH, sanitizeTextInput } from "../../utils/textSanitizer";
import "./FormSelectOrCreate.scss";
import { FormSelectOrCreateProps } from "./types";
import { IonLabel } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const defaultOptions: Option[] = [createOption("")];

const FormSelectOrCreate: React.FC<FormSelectOrCreateProps> = ({
  creatable,
  options: propsOptions,
  option,
  isLoading: loading,
  formMethods,
  fieldProps,
  validationProps,
  enableMulti,
  filterOption,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [value, setValue] = useState<Option | null>(null);

  const { control, errors, setValue: RHF_setValue } = formMethods;
  const { name, label, placeholder } = fieldProps;
  const { errorMessage } = validationProps || {};
  const isMultiEnabled = enableMulti?.isEnabled;

  const onChange = (value) => {
    if (isMultiEnabled) {
      const newOptions = Array.isArray(value)
        ? value.map((val) => {
            // Crea un nuevo objeto de tipo Option si no es uno ya
            if (typeof val === "string") {
              return createOption(sanitizeTextInput(val));
            }

            return {
              ...val,
              label: sanitizeTextInput(val?.label),
            };
          })
        : []; // Si `value` no es un array, asigna un array vacío

      enableMulti?.setter(newOptions);
    } else {
      setValue(
        value
          ? {
              ...value,
              label: sanitizeTextInput(value?.label),
            }
          : value
      );
    }
  };

  const handleCreate = (inputValue: string) => {
    const sanitizedInput = sanitizeTextInput(inputValue);

    if (!sanitizedInput) {
      return;
    }

    setIsLoading(true);
    try {
      setTimeout(() => {
        const newOption = createOption(sanitizedInput);
        // Añadir la nueva opción a las opciones disponibles
        setOptions((prev) => [...prev, newOption]);

        if (enableMulti?.isEnabled) {
          enableMulti.setter((prev) => [...prev, newOption]);
        } else {
          // Si no está habilitado, simplemente establece el valor
          setValue(newOption);
        }
      }, 500);
    } catch (error) {
      console.error("Hubo un error al crear la opción:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (propsOptions) {
      const options =
        propsOptions?.map((opt) =>
          //TODO QUE LEA DINAMICAMENTE CAMPOS DISTINTOS DE LABEL Y VALUE EN INTERFAZ OPTION
          createOption(opt.label, opt.sinonimos, opt.nombre_comercial, opt.drogas, opt.tipo_accion)
        ) || defaultOptions;
      setOptions(options);
    }
  }, [propsOptions]);

  //New value
  useEffect(() => {
    RHF_setValue(name, sanitizeTextInput(value?.label));
  }, [value]);
  //

  //On edit
  useEffect(() => {
    option && setValue(option);
  }, [option]);
  //

  const commonProps = {
    options,
    noOptionsMessage: () => "No se encontraron opciones",
    isClearable: true,
    isLoading: isLoading || loading,
    loadingMessage: () => "Cargando...",
    isDisabled: isLoading,
    placeholder,
    filterOption: (candidate, inputValue) => {
      // Si filterOption no está definido, usa comparación normalizada
      if (!filterOption) {
        return normalizeText(candidate.label).includes(normalizeText(inputValue));
      }
      // Usa el filtro personalizado si se proporciona
      return filterOption(candidate.data, inputValue);
    },
    theme: (theme) => ({
      ...theme,
      borderRadius: 5,
      colors: {
        ...theme.colors,
        primary25: "#1ea473",
        primary: "#444444",
      },
    }),
    styles: {
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
    },
  };

  const SelectComponent: React.ComponentType<any> = creatable ? CreatableSelect : Select;

  return (
    <Controller
      name={name}
      control={control}
      errors={errors}
      rules={{
        required: {
          value: !!validationProps,
          message: errorMessage ?? "",
        },
        maxLength: {
          value: MAX_TEXT_LENGTH,
          message: `Maximo ${MAX_TEXT_LENGTH} caracteres`,
        },
        validate: (value: string) => {
          if (value && value.length > MAX_TEXT_LENGTH) {
            return `Maximo ${MAX_TEXT_LENGTH} caracteres`;
          }

          if (value && value !== sanitizeTextInput(value)) {
            return "El campo contiene caracteres no permitidos";
          }

          if (validationProps?.validate) {
            return validationProps.validate(value);
          }

          return true;
        },
      }}
      render={() => (
        <div className="form-select-or-create">
          {label && (
            <div className="form-select-or-create__label">
              <IonLabel>{label}</IonLabel>
            </div>
          )}
          <SelectComponent
            {...commonProps}
            isMulti={isMultiEnabled}
            value={isMultiEnabled ? (enableMulti?.state as Option[]) : value}
            onChange={(value) => onChange(value)}
            onCreateOption={creatable ? handleCreate : undefined}
            formatCreateLabel={creatable ? (inputValue) => `Crear ${inputValue}` : undefined}
          />
          <div
            className="form-input__error-message"
            style={getErrors(errors, name) ? { opacity: 1 } : undefined}
          >
            {getErrors(errors, name)?.message}
          </div>
        </div>
      )}
    />
  );
};

export default FormSelectOrCreate;
